"use client";
import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";

const API_BASE = "https://erp-production-72da01c8e651.herokuapp.com/api";

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmt = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const fmtExact = (n) =>
  `$${Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const pct = (a, b) => (b > 0 ? Math.min(100, (a / b) * 100) : 0);

// ─── Cost bar: green=charged, yellow=pending, gray=remaining ────────────────
function CostBar({ budget, charged, pending, height = "h-2" }) {
  const chargedPct = pct(charged, budget);
  const pendingPct = pct(pending, budget);
  const overBudget = charged + pending > budget && budget > 0;
  return (
    <div
      className={`w-full bg-gray-700 rounded-full ${height} overflow-hidden flex`}
    >
      <div
        className={`h-full transition-all duration-500 ${overBudget ? "bg-red-500" : "bg-green-500"}`}
        style={{ width: `${Math.min(100, chargedPct)}%` }}
      />
      <div
        className="h-full bg-yellow-400/80 transition-all duration-500"
        style={{ width: `${Math.min(100 - chargedPct, pendingPct)}%` }}
      />
    </div>
  );
}

// ─── Section summary tile ────────────────────────────────────────────────────
function SummaryTile({
  label,
  value,
  sub,
  color = "text-white",
  small = false,
}) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
        {label}
      </span>
      <span
        className={`font-bold tabular-nums ${small ? "text-sm" : "text-lg"} ${color}`}
      >
        {value}
      </span>
      {sub && <span className="text-[10px] text-gray-400">{sub}</span>}
    </div>
  );
}

// ─── Section header (Labour / Materials) ────────────────────────────────────
function SectionHeader({
  title,
  budget,
  charged,
  pending,
  remaining,
  count,
  icon,
}) {
  const chargedPct = pct(charged, budget);
  const pendingPct = pct(pending, budget);
  const overBudget = charged > budget && budget > 0;
  return (
    <div className="mb-3">
      <div className="flex items-center gap-3 mb-2">
        <span className="text-lg">{icon}</span>
        <h2 className="text-base font-bold text-white tracking-tight">
          {title}
        </h2>
        <span className="text-xs text-gray-400">
          {count} item{count !== 1 ? "s" : ""}
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-3 bg-gray-800 rounded-lg p-4 border border-gray-700">
        <SummaryTile
          label="Total Estimated Project Cost"
          value={fmt(budget)}
          color="text-gray-200"
        />
        <SummaryTile
          label="Cost Incurred"
          value={fmt(charged)}
          sub={`${chargedPct.toFixed(1)}% of budget`}
          color={overBudget ? "text-red-400" : "text-green-400"}
        />
        <SummaryTile
          label="Committed Costs"
          value={fmt(pending)}
          sub={`${pendingPct.toFixed(1)}%`}
          color="text-yellow-400"
        />
        <SummaryTile
          label="Remaining"
          value={fmt(remaining)}
          color="text-orange-300"
        />
      </div>
      <CostBar
        budget={budget}
        charged={charged}
        pending={pending}
        height="h-3"
      />
    </div>
  );
}

// ─── Accordion card wrapper ──────────────────────────────────────────────────
function AccordionCard({ isOpen, onToggle, header, children }) {
  return (
    <div
      className={`rounded-lg border transition-colors ${isOpen ? "border-gray-500 bg-gray-800" : "border-gray-700 bg-gray-800/60 hover:border-gray-600"}`}
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        {header}
        <svg
          className={`w-4 h-4 text-gray-300 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      {isOpen && (
        <div className="px-4 pb-4 border-t border-gray-700">{children}</div>
      )}
    </div>
  );
}

// ─── Type badge ──────────────────────────────────────────────────────────────
function TypeBadge({ type }) {
  const cfg = {
    piecework: "bg-purple-700 text-purple-200 border-purple-500",
    hourly: "bg-blue-700 text-blue-200 border-blue-500",
    subcontractor: "bg-teal-700 text-teal-200 border-teal-500",
  };
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${cfg[type] || "bg-gray-600 text-gray-200 border-gray-500"}`}
    >
      {type}
    </span>
  );
}

// ─── Mini stacked bar (inline table) ─────────────────────────────────────────
function MiniBar({ budget, charged, pending }) {
  const cp = pct(charged, budget);
  const pp = pct(pending, budget);
  const over = charged + pending > budget && budget > 0;
  return (
    <div className="w-24 bg-gray-700 rounded-full h-1.5 overflow-hidden flex inline-flex">
      <div
        className={`h-full ${over ? "bg-red-500" : "bg-green-500"}`}
        style={{ width: `${Math.min(100, cp)}%` }}
      />
      <div
        className="h-full bg-yellow-400/80"
        style={{ width: `${Math.min(100 - cp, pp)}%` }}
      />
    </div>
  );
}

function useWIPItems(woId, token) {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  const woIdStr = useMemo(() => {
    if (!woId) return "";
    if (typeof woId === "string") return woId;
    if (woId._id) return String(woId._id);
    return String(woId);
  }, [woId]);

  useEffect(() => {
    if (!woIdStr) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/workorders2/${woIdStr}/wip-items`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        if (!cancelled) setResult(res.data);
      } catch (err) {
        console.error("useWIPItems error:", err);
        if (!cancelled)
          setResult({
            success: false,
            items: [],
            logCount: 0,
            timesheetSummary: null,
          });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [woIdStr, token]);

  return { result, loading, woIdStr };
}

// ─── Piecework WO drill-down ──────────────────────────────────────────────────
function PieceworkDrillDown({ woId, projectId, token }) {
  const { result, loading } = useWIPItems(woId, token);

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">
        Loading items…
      </div>
    );
  if (!result) return null;

  const active = (result.items || []).filter(
    (i) => !i.closedOutBySizeChange && !i.isHistoricalPriceLine,
  );

  const totals = active.reduce(
    (acc, item) => {
      const netQty = item.netRequiredQty ?? 0;
      const charged = item.qtyOrdered || 0;
      const pending = item.qtyPending || 0;
      const rate = item.unitRate || 0;
      return {
        charged: acc.charged + charged,
        pending: acc.pending + pending,
        budget: acc.budget + netQty * rate,
        chargedAmt: acc.chargedAmt + charged * rate,
        pendingAmt: acc.pendingAmt + pending * rate,
      };
    },
    { charged: 0, pending: 0, budget: 0, chargedAmt: 0, pendingAmt: 0 },
  );

  return (
    <div className="mt-3">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 bg-gray-900/60 rounded-lg p-3 border border-gray-700 mb-3">
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Budget
          </div>
          <div className="text-base font-bold text-gray-200">
            {fmt(totals.budget)}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Cost Incurred
          </div>
          <div className="text-base font-bold text-green-400">
            {fmt(totals.chargedAmt)}
          </div>
          <div className="text-[10px] text-gray-400">
            {totals.charged.toFixed(2)} qty
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Committed Costs
          </div>
          <div className="text-base font-bold text-yellow-400">
            {fmt(totals.pendingAmt)}
          </div>
          <div className="text-[10px] text-gray-400">
            {totals.pending.toFixed(2)} qty
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Remaining
          </div>
          <div className="text-base font-bold text-orange-300">
            {fmt(
              Math.max(
                0,
                totals.budget - totals.chargedAmt - totals.pendingAmt,
              ),
            )}
          </div>
        </div>
        <div>
          <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
            Logs
          </div>
          <div className="text-base font-bold text-blue-400">
            {result.logCount || 0}
          </div>
          <div className="text-[10px] text-gray-400">{active.length} items</div>
        </div>
      </div>

      <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
        Line Items — charged and pending from pieceworker logs
      </div>

      {active.length === 0 ? (
        <div className="py-8 text-center text-gray-500 text-sm border border-gray-700 rounded">
          No items found for this work order.
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-700">
          <table className="w-full text-xs">
            <thead className="bg-gray-900/80">
              <tr>
                {[
                  "Description",
                  "Section",
                  "Bid Area",
                  "Net Req.",
                  "UOM",
                  "Charged Qty",
                  "Pending Qty",
                  "Remaining",
                  "Rate",
                  "Budget $",
                  "Cost Incurred $",
                  "Committed $",
                  "Progress",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {active.map((item, i) => {
                const charged = item.qtyOrdered || 0;
                const pending = item.qtyPending || 0;
                const netQty = item.netRequiredQty ?? 0;
                const remaining = Math.max(0, netQty - charged - pending);
                const rate = item.unitRate || 0;
                const budget = netQty * rate;
                const chargedAmt = charged * rate;
                const pendingAmt = pending * rate;
                const chargedPct = pct(charged, netQty);
                const pendingPct2 = pct(pending, netQty);
                return (
                  <tr key={i} className="hover:bg-gray-700/30">
                    <td
                      className="px-2 py-2 text-gray-200 max-w-[200px] truncate"
                      title={item.costCodeDescription || item.itemDescription}
                    >
                      {item.costCodeDescription || item.itemDescription || "—"}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.sectionNumber || "—"}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.bidArea || "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-200 font-medium">
                      {netQty.toFixed(2)}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.uom || "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-green-400 font-medium">
                      {charged > 0 ? charged.toFixed(2) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-yellow-400">
                      {pending > 0 ? pending.toFixed(2) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      {remaining > 0 ? (
                        <span className="text-orange-300">
                          {remaining.toFixed(2)}
                        </span>
                      ) : (
                        <span className="text-green-400 font-bold">✓</span>
                      )}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-300">
                      {fmtExact(rate)}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-200">
                      {fmt(budget)}
                    </td>
                    <td className="px-2 py-2 text-right text-green-400 font-semibold">
                      {chargedAmt > 0 ? fmt(chargedAmt) : "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-yellow-400">
                      {pendingAmt > 0 ? fmt(pendingAmt) : "—"}
                    </td>
                    <td className="px-2 py-2">
                      <div className="flex items-center gap-2">
                        <div className="w-20 bg-gray-700 rounded-full h-1.5 overflow-hidden flex">
                          <div
                            className="h-full bg-green-500"
                            style={{ width: `${Math.min(100, chargedPct)}%` }}
                          />
                          <div
                            className="h-full bg-yellow-400/80"
                            style={{
                              width: `${Math.min(100 - chargedPct, pendingPct2)}%`,
                            }}
                          />
                        </div>
                        <span
                          className={`text-[10px] font-semibold ${chargedPct >= 90 ? "text-green-400" : chargedPct >= 50 ? "text-blue-400" : "text-gray-400"}`}
                        >
                          {chargedPct.toFixed(0)}%
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Hourly WO drill-down ──────────────────────────────────────────────────────
function HourlyDrillDown({ woId, projectId, token, contractHourlyRate }) {
  const { result, loading } = useWIPItems(woId, token);
  const woIdStr = useMemo(() => {
    if (!woId) return "";
    if (typeof woId === "string") return woId;
    if (woId._id) return String(woId._id);
    return String(woId);
  }, [woId]);

  const [progressMaps, setProgressMaps] = useState(null);

  useEffect(() => {
    if (!woIdStr) return;
    let cancelled = false;
    const load = async () => {
      try {
        const res = await axios.get(
          `${API_BASE}/progressLogs?workOrder=${woIdStr}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const logs = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const approvedMap = {};
        const pendingMap = {};
        logs.forEach((log) => {
          if (!["approved", "pending"].includes(log.status)) return;
          const target = log.status === "approved" ? approvedMap : pendingMap;
          (log.lineItems || []).forEach((li) => {
            const key = String(li.lineItemId?.$oid || li.lineItemId);
            target[key] = (target[key] || 0) + (li.qtyInstalled || 0);
          });
        });
        if (!cancelled)
          setProgressMaps({ approvedMap, pendingMap, logCount: logs.length });
      } catch {
        if (!cancelled)
          setProgressMaps({ approvedMap: {}, pendingMap: {}, logCount: 0 });
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [woIdStr, token]);

  if (loading || !progressMaps)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
    );
  if (!result) return null;

  const { timesheetSummary } = result;
  const { approvedMap, pendingMap } = progressMaps;

  const active = (result.items || []).filter(
    (i) => !i.closedOutBySizeChange && !i.isHistoricalPriceLine,
  );

  return (
    <div className="mt-3 space-y-4">
      {timesheetSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 bg-gray-900/60 rounded-lg p-3 border border-gray-700">
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
              Cost Incurred (Approved)
            </div>
            <div className="text-base font-bold text-orange-400">
              {fmtExact(timesheetSummary.approved?.totalCost || 0)}
            </div>
            <div className="text-[10px] text-gray-400">
              {timesheetSummary.approved?.hours || 0}h ·{" "}
              {timesheetSummary.approved?.count || 0} sheets
            </div>
          </div>
          {(timesheetSummary.pending?.count || 0) > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
                Committed Costs (Pending)
              </div>
              <div className="text-base font-bold text-yellow-400">
                {fmtExact(timesheetSummary.pending?.totalCost || 0)}
              </div>
              <div className="text-[10px] text-gray-400">
                {timesheetSummary.pending?.hours || 0}h ·{" "}
                {timesheetSummary.pending?.count || 0} sheets
              </div>
            </div>
          )}
          <div>
            <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
              Progress Logs
            </div>
            <div className="text-base font-bold text-blue-400">
              {progressMaps.logCount}
            </div>
            <div className="text-[10px] text-gray-400">submitted</div>
          </div>
          {(timesheetSummary.approved?.burdenCost || 0) > 0 && (
            <div>
              <div className="text-[10px] text-gray-400 uppercase tracking-widest mb-1">
                Breakdown
              </div>
              <div className="text-[10px] text-gray-400 space-y-0.5">
                <div>Base: {fmtExact(timesheetSummary.approved.subtotal)}</div>
                <div>
                  Burden: {fmtExact(timesheetSummary.approved.burdenCost)}
                </div>
                {(timesheetSummary.approved?.wsibCost || 0) > 0 && (
                  <div>
                    WSIB: {fmtExact(timesheetSummary.approved.wsibCost)}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div>
        <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
          Progress by Line Item (qty installed / budget qty)
        </div>
        {active.length === 0 ? (
          <div className="py-8 text-center text-gray-500 text-sm border border-gray-700 rounded">
            No items found for this work order.
          </div>
        ) : (
          <div className="overflow-x-auto rounded border border-gray-700">
            <table className="w-full text-xs">
              <thead className="bg-gray-900/80">
                <tr>
                  {[
                    "Description",
                    "Section",
                    "Bid Area",
                    "Net Req.",
                    "UOM",
                    "Approved Qty",
                    "Pending Qty",
                    "Outstanding",
                    "Rate",
                    "Budget Cost",
                    "Progress Cost",
                    "Progress",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700/50">
                {active.map((item, i) => {
                  const key = String(item.lineItemId || "");
                  const approved = approvedMap[key] || 0;
                  const pending = pendingMap[key] || 0;
                  const netQty = item.netRequiredQty ?? 0;
                  const outstanding = Math.max(0, netQty - approved - pending);
                  const rate = item.unitRate || 0;
                  const budgetCost = netQty * rate;
                  const approvedCost = approved * rate;
                  const approvedPct = pct(approved, netQty);
                  const pendingPct2 = pct(pending, netQty);
                  return (
                    <tr key={i} className="hover:bg-gray-700/30">
                      <td
                        className="px-2 py-2 text-gray-200 max-w-[180px] truncate"
                        title={item.costCodeDescription || item.itemDescription}
                      >
                        {item.costCodeDescription ||
                          item.itemDescription ||
                          "—"}
                      </td>
                      <td className="px-2 py-2 text-gray-400">
                        {item.sectionNumber || "—"}
                      </td>
                      <td className="px-2 py-2 text-gray-400">
                        {item.bidArea || "—"}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-200 font-medium">
                        {netQty.toFixed(2)}
                      </td>
                      <td className="px-2 py-2 text-gray-400">
                        {item.uom || "—"}
                      </td>
                      <td className="px-2 py-2 text-right text-green-400 font-medium">
                        {approved > 0 ? approved.toFixed(2) : "—"}
                      </td>
                      <td className="px-2 py-2 text-right text-yellow-400">
                        {pending > 0 ? pending.toFixed(2) : "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        {outstanding > 0 ? (
                          <span className="text-orange-300">
                            {outstanding.toFixed(2)}
                          </span>
                        ) : (
                          <span className="text-green-400 font-bold">✓</span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-300">
                        {fmtExact(rate)}
                      </td>
                      <td className="px-2 py-2 text-right text-gray-200">
                        {fmt(budgetCost)}
                      </td>
                      <td className="px-2 py-2 text-right text-green-400">
                        {approvedCost > 0 ? fmt(approvedCost) : "—"}
                      </td>
                      <td className="px-2 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-700 rounded-full h-1.5 overflow-hidden flex">
                            <div
                              className="h-full bg-green-500"
                              style={{
                                width: `${Math.min(100, approvedPct)}%`,
                              }}
                            />
                            <div
                              className="h-full bg-yellow-400/80"
                              style={{
                                width: `${Math.min(100 - approvedPct, pendingPct2)}%`,
                              }}
                            />
                          </div>
                          <span
                            className={`text-[10px] font-semibold ${approvedPct >= 90 ? "text-green-400" : approvedPct >= 50 ? "text-blue-400" : "text-gray-400"}`}
                          >
                            {approvedPct.toFixed(0)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Subcontractor WO drill-down ───────────────────────────────────────────────
function SubcontractorDrillDown({ woId, projectId, token }) {
  const { result, loading } = useWIPItems(woId, token);

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
    );
  if (!result) return null;

  const active = (result.items || []).filter(
    (i) => !i.closedOutBySizeChange && !i.isHistoricalPriceLine,
  );

  return (
    <div className="mt-3">
      <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
        Line Items — {active.length} items
      </div>
      {active.length === 0 ? (
        <div className="py-8 text-center text-gray-500 text-sm border border-gray-700 rounded">
          No items found for this work order.
        </div>
      ) : (
        <div className="overflow-x-auto rounded border border-gray-700">
          <table className="w-full text-xs">
            <thead className="bg-gray-900/80">
              <tr>
                {[
                  "Description",
                  "Section",
                  "Bid Area",
                  "Net Qty",
                  "UOM",
                  "Rate",
                  "Budget Amount",
                ].map((h) => (
                  <th
                    key={h}
                    className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/50">
              {active.map((item, i) => {
                const netQty = item.netRequiredQty ?? 0;
                const rate = item.unitRate || 0;
                return (
                  <tr key={i} className="hover:bg-gray-700/30">
                    <td className="px-2 py-2 text-gray-200 max-w-[220px] truncate">
                      {item.costCodeDescription || item.itemDescription || "—"}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.sectionNumber || "—"}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.bidArea || "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-200">
                      {netQty.toFixed(2)}
                    </td>
                    <td className="px-2 py-2 text-gray-400">
                      {item.uom || "—"}
                    </td>
                    <td className="px-2 py-2 text-right text-gray-300">
                      {fmtExact(rate)}
                    </td>
                    <td className="px-2 py-2 text-right font-medium text-orange-400">
                      {fmt(netQty * rate)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ─── Material PO drill-down ────────────────────────────────────────────────────
function MaterialDrillDown({ poId, projectId, token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const res = await axios.get(
          `${API_BASE}/net-assemblies/project/${projectId}`,
          { headers, params: { category: "M", linkedPO: poId } },
        );
        setData(res.data.data || []);
      } catch {
        setData([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [poId, projectId, token]);

  if (loading)
    return (
      <div className="py-6 text-center text-gray-400 text-sm">Loading…</div>
    );
  if (!data) return null;

  const active = data.filter(
    (i) => !i.closedOutBySizeChange && !i.isHistoricalPriceLine,
  );

  return (
    <div className="mt-3">
      <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-2">
        Material Items — {active.length} items
      </div>
      <div className="overflow-x-auto rounded border border-gray-700">
        <table className="w-full text-xs">
          <thead className="bg-gray-900/80">
            <tr>
              {[
                "Description",
                "Condition",
                "Section",
                "Size",
                "Vendor",
                "Net Qty",
                "UOM",
                "Ordered",
                "Pending",
                "Remaining",
                "Price",
                "Budget $",
                "Ordered $",
                "Progress",
              ].map((h) => (
                <th
                  key={h}
                  className="px-2 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700/50">
            {active.map((item, i) => {
              const netQty = item.netRequiredQty || 0;
              const ordered = item.qtyOrdered || 0;
              const pending = item.qtyPending || 0;
              const remaining = item.netRemaining || 0;
              const price = item.latestPrice || item.price || 0;
              const coverage =
                item.latestCoveragePerUnit ?? item.coveragePerUnit ?? 1;
              const safeCov =
                !coverage || isNaN(Number(coverage)) || Number(coverage) <= 0
                  ? 1
                  : Number(coverage);
              const budget = netQty * price * safeCov;
              const orderedAmt = ordered * price * safeCov;
              const orderedPct = pct(ordered, netQty);
              const pendingPct = pct(pending, netQty);
              return (
                <tr
                  key={i}
                  className={`hover:bg-gray-700/30 ${item.hasVariance ? "border-l-2 border-l-purple-500/40" : ""}`}
                >
                  <td
                    className="px-2 py-2 text-gray-200 max-w-[180px] truncate"
                    title={item.costCodeDescription || item.itemDescription}
                  >
                    {item.costCodeDescription || item.itemDescription || "—"}
                  </td>
                  <td className="px-2 py-2 text-gray-400 max-w-[120px] truncate">
                    {item.conditionName || "—"}
                  </td>
                  <td className="px-2 py-2 text-gray-400">
                    {item.sectionNumber || "—"}
                  </td>
                  <td className="px-2 py-2 text-gray-300">
                    {item.latestSize || item.size || "—"}
                  </td>
                  <td className="px-2 py-2 text-gray-400 max-w-[100px] truncate">
                    {typeof item.vendor === "object"
                      ? item.vendor?.companyName
                      : "—"}
                  </td>
                  <td className="px-2 py-2 text-right text-gray-200 font-medium">
                    {netQty.toFixed(2)}
                  </td>
                  <td className="px-2 py-2 text-gray-400">{item.uom || "—"}</td>
                  <td className="px-2 py-2 text-right text-green-400 font-medium">
                    {ordered > 0 ? ordered.toFixed(2) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right text-yellow-400">
                    {pending > 0 ? pending.toFixed(2) : "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {remaining > 0 ? (
                      <span className="text-orange-300">
                        {remaining.toFixed(2)}
                      </span>
                    ) : (
                      <span className="text-green-400 font-bold">✓</span>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right text-gray-300">
                    {fmtExact(price)}
                  </td>
                  <td className="px-2 py-2 text-right text-gray-200">
                    {fmt(budget)}
                  </td>
                  <td className="px-2 py-2 text-right text-green-400">
                    {orderedAmt > 0 ? fmt(orderedAmt) : "—"}
                  </td>
                  <td className="px-2 py-2">
                    <div className="flex items-center gap-2">
                      <div className="w-20 bg-gray-700 rounded-full h-1.5 overflow-hidden flex">
                        <div
                          className="h-full bg-green-500"
                          style={{ width: `${Math.min(100, orderedPct)}%` }}
                        />
                        <div
                          className="h-full bg-yellow-400/80"
                          style={{
                            width: `${Math.min(100 - orderedPct, pendingPct)}%`,
                          }}
                        />
                      </div>
                      <span
                        className={`text-[10px] font-semibold ${orderedPct >= 90 ? "text-green-400" : orderedPct >= 50 ? "text-blue-400" : "text-gray-400"}`}
                      >
                        {orderedPct.toFixed(0)}%
                      </span>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── WO accordion header ─────────────────────────────────────────────────────
function WOAccordionHeader({
  wo,
  budgetAmt,
  chargedAmt,
  pendingAmt,
  remainingAmt,
}) {
  const overBudget = chargedAmt > budgetAmt && budgetAmt > 0;
  const chargedPct = pct(chargedAmt, budgetAmt);
  return (
    <div className="flex-1 flex items-center gap-3 min-w-0 pr-4">
      <TypeBadge type={wo.type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white truncate">
            {wo.title}
          </span>
          <span className="text-xs text-gray-400">WO#{wo.woNumber}</span>
          {wo.union && wo.union !== "non-union" && (
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
              {wo.union}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 flex-wrap">
          <span className="text-[11px] text-gray-400">
            Budget{" "}
            <span className="text-gray-200 font-medium">{fmt(budgetAmt)}</span>
          </span>
          <span className="text-[11px] text-gray-400">
            Cost Incurred{" "}
            <span
              className={`font-medium ${overBudget ? "text-red-400" : "text-green-400"}`}
            >
              {fmt(chargedAmt)}
            </span>
          </span>
          {pendingAmt > 0 && (
            <span className="text-[11px] text-gray-400">
              Committed{" "}
              <span className="text-yellow-400 font-medium">
                {fmt(pendingAmt)}
              </span>
            </span>
          )}
          <span className="text-[11px] text-gray-400">
            Remaining{" "}
            <span className="text-orange-300 font-medium">
              {fmt(remainingAmt)}
            </span>
          </span>
        </div>
      </div>
      <div className="w-32 shrink-0">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>{chargedPct.toFixed(0)}% incurred</span>
        </div>
        <CostBar
          budget={budgetAmt}
          charged={chargedAmt}
          pending={pendingAmt}
          height="h-1.5"
        />
      </div>
    </div>
  );
}

// ─── Hourly WO accordion header ───────────────────────────────────────────────
function HourlyWOAccordionHeader({
  wo,
  budgetAmt,
  chargedAmt,
  pendingAmt,
  remainingAmt,
  stats,
}) {
  const { progressPct } = stats || {};
  const costPct =
    budgetAmt > 0 && chargedAmt > 0
      ? Math.min(999, (chargedAmt / budgetAmt) * 100)
      : null;
  const overBudget = costPct != null && costPct > 100;

  const progressColor =
    progressPct == null
      ? "text-gray-500"
      : progressPct >= 90
        ? "text-green-400"
        : progressPct >= 50
          ? "text-blue-400"
          : "text-yellow-400";

  const costColor =
    costPct == null
      ? "text-gray-500"
      : overBudget
        ? "text-red-400"
        : costPct >= 90
          ? "text-orange-400"
          : "text-orange-300";

  return (
    <div className="flex-1 flex items-center gap-3 min-w-0 pr-4">
      <TypeBadge type={wo.type} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold text-white truncate">
            {wo.title}
          </span>
          <span className="text-xs text-gray-400">WO#{wo.woNumber}</span>
          {wo.union && wo.union !== "non-union" && (
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded">
              {wo.union}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 flex-wrap">
          <span className="text-[11px] text-gray-400">
            Budget{" "}
            <span className="text-gray-200 font-medium">{fmt(budgetAmt)}</span>
          </span>
          {chargedAmt > 0 && (
            <span className="text-[11px] text-gray-400">
              Cost Incurred{" "}
              <span
                className={`font-medium ${overBudget ? "text-red-400" : "text-orange-400"}`}
              >
                {fmt(chargedAmt)}
              </span>
            </span>
          )}
          {pendingAmt > 0 && (
            <span className="text-[11px] text-gray-400">
              Committed{" "}
              <span className="text-yellow-400 font-medium">
                {fmt(pendingAmt)}
              </span>
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <div className="flex flex-col items-center gap-0.5 min-w-[52px]">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">
            Progress
          </span>
          <span className={`text-sm font-bold ${progressColor}`}>
            {progressPct != null ? `${progressPct.toFixed(0)}%` : "—"}
          </span>
          {progressPct != null && (
            <div className="w-12 bg-gray-700 rounded-full h-1 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  progressPct >= 90
                    ? "bg-green-500"
                    : progressPct >= 50
                      ? "bg-blue-500"
                      : "bg-yellow-400"
                }`}
                style={{ width: `${Math.min(100, progressPct)}%` }}
              />
            </div>
          )}
        </div>
        <div className="w-px h-8 bg-gray-700" />
        <div className="flex flex-col items-center gap-0.5 min-w-[52px]">
          <span className="text-[9px] text-gray-500 uppercase tracking-wide">
            Cost
          </span>
          <span className={`text-sm font-bold ${costColor}`}>
            {costPct != null
              ? `${costPct > 999 ? ">999" : costPct.toFixed(0)}%`
              : "—"}
          </span>
          {costPct != null && (
            <div className="w-12 bg-gray-700 rounded-full h-1 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${overBudget ? "bg-red-500" : "bg-orange-400"}`}
                style={{ width: `${Math.min(100, costPct)}%` }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── PO accordion header ──────────────────────────────────────────────────────
function POAccordionHeader({
  po,
  budgetAmt,
  orderedAmt,
  pendingAmt,
  remainingAmt,
}) {
  const overBudget = orderedAmt > budgetAmt && budgetAmt > 0;
  const orderedPct = pct(orderedAmt, budgetAmt);
  return (
    <div className="flex-1 flex items-center gap-3 min-w-0 pr-4">
      <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-orange-700 text-orange-200 border-orange-500">
        PO
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-semibold text-white truncate">
            {po.title}
          </span>
          <span className="text-xs text-gray-400">PO#{po.poNumber}</span>
          {po.primaryVendor && (
            <span className="text-[10px] px-1.5 py-0.5 bg-gray-700 text-gray-400 rounded truncate max-w-[100px]">
              {po.primaryVendor?.companyName || po.primaryVendor}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4 mt-1 flex-wrap">
          <span className="text-[11px] text-gray-400">
            Budget{" "}
            <span className="text-gray-200 font-medium">{fmt(budgetAmt)}</span>
          </span>
          <span className="text-[11px] text-gray-400">
            Cost Incurred{" "}
            <span
              className={`font-medium ${overBudget ? "text-red-400" : "text-green-400"}`}
            >
              {fmt(orderedAmt)}
            </span>
          </span>
          {pendingAmt > 0 && (
            <span className="text-[11px] text-gray-400">
              Committed{" "}
              <span className="text-yellow-400 font-medium">
                {fmt(pendingAmt)}
              </span>
            </span>
          )}
          <span className="text-[11px] text-gray-400">
            Remaining{" "}
            <span className="text-orange-300 font-medium">
              {fmt(remainingAmt)}
            </span>
          </span>
        </div>
      </div>
      <div className="w-32 shrink-0">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1">
          <span>{orderedPct.toFixed(0)}% incurred</span>
        </div>
        <CostBar
          budget={budgetAmt}
          charged={orderedAmt}
          pending={pendingAmt}
          height="h-1.5"
        />
      </div>
    </div>
  );
}

// ─── Main WIP Screen ──────────────────────────────────────────────────────────
export default function WIPScreen() {
  const { id: projectId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [netMaterials, setNetMaterials] = useState([]);
  const [netLabour, setNetLabour] = useState([]);
  const [projectSummary, setProjectSummary] = useState(null);
  const [hourlyStats, setHourlyStats] = useState({});
  const [openAccordions, setOpenAccordions] = useState({});
  const [activeTab, setActiveTab] = useState("labour");
  const [materialOrders, setMaterialOrders] = useState([]);
  const [coBreakdownOpen, setCoBreakdownOpen] = useState(false);

  // ── NEW STATE for contract & billing ──────────────────────────────────────
  // changeOrdersFull: full CO list with items (for budget cost calculation)
  // sovData: SOV list + project data (for billing totals)
  const [changeOrdersFull, setChangeOrdersFull] = useState([]);
  const [sovData, setSovData] = useState({
    scheduleOfValues: [],
    project: null,
  });

  // ── Load all data on mount ─────────────────────────────────────────────────
  // CHANGED: added changeOrders2 and sov2 fetches to the existing Promise.all
  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const [
          woRes,
          poRes,
          summaryRes,
          netMatRes,
          netLabRes,
          moRes,
          // ── NEW: two extra parallel fetches ──
          coRes,
          sovRes,
        ] = await Promise.all([
          axios.get(`${API_BASE}/workorders2/project/${projectId}`, {
            headers,
          }),
          axios.get(`${API_BASE}/purchaseOrders2/project/${projectId}`, {
            headers,
          }),
          axios.get(`${API_BASE}/net-assemblies/project-summary/${projectId}`, {
            headers,
          }),
          axios.get(`${API_BASE}/net-assemblies/project/${projectId}`, {
            headers,
            params: { category: "M" },
          }),
          axios.get(`${API_BASE}/net-assemblies/project/${projectId}`, {
            headers,
            params: { category: "L" },
          }),
          axios.get(`${API_BASE}/materialOrders2/project/${projectId}`, {
            headers,
          }),
          // Full change orders with items for budget cost calc
          axios.get(`${API_BASE}/changeOrders2/project/${projectId}`, {
            headers,
          }),
          // SOV data for billing totals + project contract value
          axios.get(`${API_BASE}/sovs2/project/${projectId}`, { headers }),
        ]);

        setWorkOrders(woRes.data?.workOrders || woRes.data || []);
        setPurchaseOrders(poRes.data?.purchaseOrders || poRes.data || []);
        setProjectSummary(summaryRes.data);
        setNetMaterials(netMatRes.data?.data || []);
        setNetLabour(netLabRes.data?.data || []);
        setMaterialOrders(Array.isArray(moRes.data) ? moRes.data : []);
        // ── NEW: store CO and SOV data ──
        setChangeOrdersFull(coRes.data?.changeOrders || coRes.data || []);
        setSovData({
          scheduleOfValues: sovRes.data?.scheduleOfValues || [],
          project: sovRes.data?.project || null,
        });
      } catch (err) {
        console.error("WIP load error:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [projectId]);

  const toggleAccordion = useCallback((key) => {
    setOpenAccordions((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  // ─── Fetch hourly WO stats ────────────────────────────────────────────────
  useEffect(() => {
    const hourlyWOList = workOrders.filter((wo) => wo.type === "hourly");
    if (!hourlyWOList.length) return;

    const fetchStats = async () => {
      const results = await Promise.all(
        hourlyWOList.map(async (wo) => {
          const woId = wo._id?.toString();
          const heads = { Authorization: `Bearer ${token}` };
          try {
            const [logsRes, tsRes] = await Promise.all([
              axios
                .get(`${API_BASE}/progressLogs?workOrder=${woId}`, {
                  headers: heads,
                })
                .catch(() => ({ data: [] })),
              axios
                .get(`${API_BASE}/workorders2/${woId}/timesheet-summary`, {
                  headers: heads,
                })
                .catch(() => ({ data: null })),
            ]);

            const labItems = netLabour.filter(
              (i) =>
                i.linkedWO?.toString() === woId &&
                !i.closedOutBySizeChange &&
                !i.isHistoricalPriceLine,
            );
            const totalEstimated = labItems.reduce(
              (s, i) => s + (i.netRequiredQty ?? i.quantity ?? 0),
              0,
            );

            const logs = Array.isArray(logsRes.data)
              ? logsRes.data
              : logsRes.data?.data || [];
            const approvedMap = {};
            logs.forEach((log) => {
              if (log.status !== "approved") return;
              (log.lineItems || []).forEach((li) => {
                const k = String(li.lineItemId?.$oid || li.lineItemId);
                approvedMap[k] = (approvedMap[k] || 0) + (li.qtyInstalled || 0);
              });
            });
            const totalApproved = labItems.reduce((s, i) => {
              const k = String(i.lineItemId || i._id || "");
              return s + (approvedMap[k] || 0);
            }, 0);
            const progressPct =
              totalEstimated > 0
                ? Math.min(100, (totalApproved / totalEstimated) * 100)
                : null;

            const tsData = tsRes.data;
            const timesheetCost =
              tsData?.approved?.totalCost ?? tsData?.totalCost ?? null;
            const pendingCost = tsData?.pending?.totalCost ?? null;

            return { woId, progressPct, timesheetCost, pendingCost };
          } catch {
            return { woId, progressPct: null, timesheetCost: null };
          }
        }),
      );

      const map = {};
      results.forEach(({ woId, progressPct, timesheetCost, pendingCost }) => {
        map[woId] = { progressPct, timesheetCost, pendingCost };
      });
      setHourlyStats(map);
    };

    fetchStats();
  }, [workOrders, netLabour, token]);

  // ─── Derived: WO cost summaries ──────────────────────────────────────────
  const woSummaries = useMemo(() => {
    const woMap = {};
    netLabour.forEach((item) => {
      if (
        !item.linkedWO ||
        item.isHistoricalPriceLine ||
        item.closedOutBySizeChange
      )
        return;
      const woId = item.linkedWO?.toString();
      if (!woId) return;
      if (!woMap[woId]) woMap[woId] = { budget: 0, charged: 0, pending: 0 };
      const netQty = item.netRequiredQty ?? item.quantity ?? 0;
      const rate = item.unitRate || item.price || 0;
      woMap[woId].budget += netQty * rate;
      woMap[woId].charged += (item.qtyOrdered || 0) * rate;
      woMap[woId].pending += (item.qtyPending || 0) * rate;
    });

    return workOrders.map((wo) => {
      const woId = wo._id?.toString();
      const fromNet = woMap[woId];
      const budget =
        (fromNet?.budget > 0 ? fromNet.budget : null) ?? wo.estimatedTotal ?? 0;

      let charged, pending;
      if (wo.type === "hourly") {
        const hs = hourlyStats[woId];
        charged = hs?.timesheetCost ?? 0;
        pending = hs?.pendingCost ?? 0;
      } else {
        charged = fromNet?.charged ?? 0;
        pending = fromNet?.pending ?? 0;
      }

      const remaining = Math.max(0, budget - charged - pending);
      return {
        ...wo,
        budgetAmt: budget,
        chargedAmt: charged,
        pendingAmt: pending,
        remainingAmt: remaining,
      };
    });
  }, [workOrders, netLabour, hourlyStats]);

  // ─── Derived: PO cost summaries ──────────────────────────────────────────
  const poSummaries = useMemo(() => {
    const poMap = {};

    materialOrders.forEach((order) => {
      if (order.status === "void") return;

      const poId =
        order.purchaseOrder?._id?.toString() || order.purchaseOrder?.toString();
      if (!poId) return;

      if (!poMap[poId]) poMap[poId] = { ordered: 0, pending: 0 };

      const isDelivered = order.status === "delivered";

      const itemsTotal = (order.items || []).reduce((sum, item) => {
        const qty = parseFloat(item.quantity) || 0;
        const price = parseFloat(item.price || item.unitPrice) || 0;
        const uom = (item.uom || item.container || "").toLowerCase();
        const isCoverageUom =
          uom === "lf" ||
          uom === "sf" ||
          uom.includes("linear") ||
          uom.includes("square");
        let cov = 1;
        if (isCoverageUom) {
          const rawCov = parseFloat(item.coveragePerUnit);
          if (!isNaN(rawCov) && rawCov > 0) cov = rawCov;
        }
        return sum + qty * cov * price;
      }, 0);

      const surchargesTotal = (order.surcharges || []).reduce((sum, s) => {
        return sum + (parseFloat(s.amount || s.price || s.value) || 0);
      }, 0);

      const orderTotal = itemsTotal + surchargesTotal;

      if (isDelivered) {
        poMap[poId].ordered += orderTotal;
      } else {
        poMap[poId].pending += orderTotal;
      }
    });

    const poBudgetMap = {};
    netMaterials.forEach((item) => {
      if (!item.linkedPO || item.isHistoricalPriceLine) return;
      const poId = item.linkedPO?._id?.toString() || item.linkedPO?.toString();
      if (!poId) return;
      if (!poBudgetMap[poId]) poBudgetMap[poId] = 0;

      const price = item.latestPrice || item.price || 0;
      let cov = 1;
      const rawCov = item.latestCoveragePerUnit ?? item.coveragePerUnit;
      if (
        rawCov != null &&
        rawCov !== "" &&
        !isNaN(Number(rawCov)) &&
        Number(rawCov) > 0
      ) {
        cov = Number(rawCov);
      }
      poBudgetMap[poId] += (item.netRequiredQty || 0) * price * cov;
    });

    return purchaseOrders.map((po) => {
      const poId = po._id?.toString();
      const costs = poMap[poId] || { ordered: 0, pending: 0 };
      const budget = poBudgetMap[poId] ?? po.estimatedTotal ?? 0;
      const remaining = Math.max(0, budget - costs.ordered - costs.pending);
      return {
        ...po,
        budgetAmt: budget,
        orderedAmt: costs.ordered,
        pendingAmt: costs.pending,
        remainingAmt: remaining,
      };
    });
  }, [purchaseOrders, netMaterials, materialOrders]);

  // ─── Section totals ───────────────────────────────────────────────────────
  const labourWOs = woSummaries.filter((w) =>
    ["piecework", "hourly", "subcontractor"].includes(w.type),
  );
  const labourTotals = labourWOs.reduce(
    (acc, w) => ({
      budget: acc.budget + w.budgetAmt,
      charged: acc.charged + w.chargedAmt,
      pending: acc.pending + w.pendingAmt,
      remaining: acc.remaining + w.remainingAmt,
    }),
    { budget: 0, charged: 0, pending: 0, remaining: 0 },
  );

  const matTotals = poSummaries.reduce(
    (acc, p) => ({
      budget: acc.budget + p.budgetAmt,
      ordered: acc.ordered + p.orderedAmt,
      pending: acc.pending + p.pendingAmt,
      remaining: acc.remaining + p.remainingAmt,
    }),
    { budget: 0, ordered: 0, pending: 0, remaining: 0 },
  );

  const totalBudget =
    projectSummary?.totalEstimatedCost ||
    0 ||
    labourTotals.budget + matTotals.budget;
  const totalCharged = labourTotals.charged + matTotals.ordered;
  const totalPending = labourTotals.pending + matTotals.pending;
  const totalRemaining = Math.max(0, totalBudget - totalCharged - totalPending);
  const overallPct = pct(totalCharged, totalBudget);
  const coImpact = projectSummary?.changeOrderImpact || 0;
  const varianceImpact = projectSummary?.varianceImpact || 0;

  // ── NEW: Contract & billing derived values ─────────────────────────────────
  // Split COs by approval status
  const approvedCOs = changeOrdersFull.filter(
    (co) => co.approvalStatus === "Approved",
  );
  const pendingCOs = changeOrdersFull.filter(
    (co) => co.approvalStatus === "Pending",
  );
  const rejectedCOs = changeOrdersFull.filter(
    (co) => co.approvalStatus === "Rejected",
  );

  // Original contract value from the project record returned by the SOV endpoint
  const originalContract = parseFloat(
    sovData.project?.originalContractValue || 0,
  );

  // Sum of approved CO customer prices (what we bill the client)
  const approvedCOValue = approvedCOs.reduce(
    (s, co) => s + (co.totalCustomerPrice || co.totalPrice || 0),
    0,
  );

  // ── CO item budget cost helper ────────────────────────────────────────────
  // The backend stores `amount` as Math.abs(qty) * rate — it does NOT apply
  // the sign from deltaQty. Items where deltaQty < 0 are reductions (credits)
  // and must be subtracted, not added.
  //
  // Coverage (coveragePerUnit) is ONLY applied for genuine area/length UOMs
  // (SF, LF, sheet). A coveragePerUnit of "1000" on a BOX item means 1000
  // units per box — that is NOT a multiplier on the cost; the backend already
  // accounts for it when it sets `amount`. We therefore ignore coveragePerUnit
  // here and recalculate purely from deltaQty × unitCost, preserving the sign.
  const calcCOItemSignedCost = (item) => {
    const deltaQty = item.deltaQty ?? item.quantity ?? 0;
    const unitCost = item.unitCost || item.unitRate || 0;
    // For coverage UOMs (SF / LF / sheet) the backend multiplies by coverage.
    // Replicate that here so the math matches the approval route exactly.
    const uom = (item.uom || item.container || "").toLowerCase();
    const isCoverageUom =
      uom === "sf" ||
      uom === "lf" ||
      uom.includes("sq") ||
      uom.includes("square") ||
      uom.includes("linear") ||
      uom.includes("sheet");
    let coverage = 1;
    if (isCoverageUom) {
      const raw = parseFloat(item.coveragePerUnit);
      if (!isNaN(raw) && raw > 0) coverage = raw;
    }
    // deltaQty carries the sign: negative = reduction (credit to budget)
    return deltaQty * coverage * unitCost;
  };

  // Sum of approved CO budget costs (signed: reductions subtract, additions add)
  const approvedCOCost = approvedCOs.reduce((s, co) => {
    const itemCost = (co.items || [])
      .filter((i) => i.forBudget !== false)
      .reduce((a, i) => a + calcCOItemSignedCost(i), 0);
    return s + itemCost;
  }, 0);

  // Sum of pending CO customer prices
  const pendingCOValue = pendingCOs.reduce(
    (s, co) => s + (co.totalCustomerPrice || co.totalPrice || 0),
    0,
  );

  // Current contract value = original + all approved COs
  const currentContractValue = originalContract + approvedCOValue;

  // ── Billing totals from SOV records ──────────────────────────────────────
  const sovList = sovData.scheduleOfValues || [];

  // ── IMPORTANT: "Total Billed" = subtotal − holdback, NO tax ──────────────
  //
  // Each invoiceComponent stores three separate fields:
  //   ic.amount   = work subtotal for this period (before holdback, before tax)
  //   ic.holdback = holdback deducted (10% of amount when withHoldback is on)
  //   ic.tax      = HST/GST applied
  //   ic.total    = amount − holdback + tax  (the QB invoice total)
  //
  // The correct "total invoiced" per the SOV tab's calculateTotalInvoiced() is:
  //   subtotal − holdback  (i.e. ic.amount − ic.holdback) — no tax included.
  //
  // We derive this per component and sum across all non-voided components.

  const allInvoiceComponents = sovList.flatMap((sov) =>
    (sov.invoiceComponents || []).filter((ic) => ic.status !== "voided"),
  );

  // amount − holdback per component, summed
  const totalBilled = allInvoiceComponents.reduce(
    (s, ic) =>
      s + (parseFloat(ic.amount) || 0) - (parseFloat(ic.holdback) || 0),
    0,
  );

  const totalPaid = allInvoiceComponents.reduce(
    (s, ic) => s + (parseFloat(ic.amountPaid) || 0),
    0,
  );

  // Fallback: no QB invoiceComponents yet — sum workCompletedThisPeriod minus
  // estimated holdback from invoiced/paid SOVs (for non-QuickBooks users).
  const totalBilledFallback = sovList
    .filter((s) => ["invoiced", "paid"].includes(s.status))
    .reduce((s, sov) => {
      const subtotal = (sov.items || []).reduce(
        (a, item) => a + (parseFloat(item.workCompletedThisPeriod) || 0),
        0,
      );
      const holdback = sov.withHoldback ? subtotal * 0.1 : 0;
      return s + subtotal - holdback;
    }, 0);

  const effectiveTotalBilled =
    totalBilled > 0 ? totalBilled : totalBilledFallback;

  // Billing % = effectiveTotalBilled / currentContractValue
  const billingPct =
    currentContractValue > 0
      ? Math.min(100, (effectiveTotalBilled / currentContractValue) * 100)
      : 0;

  // WIP completion % = totalCharged / totalBudget (cost incurred vs budget)
  const wipCompletionPct =
    totalBudget > 0 ? Math.min(100, (totalCharged / totalBudget) * 100) : 0;

  // Gap: positive = overbilled (billed more than work done), negative = underbilled
  const billingGapPct = billingPct - wipCompletionPct;
  const billingGapAmt = (billingGapPct / 100) * currentContractValue;

  // Accounts receivable = billed but not yet paid
  const accountsReceivable = effectiveTotalBilled - totalPaid;

  // ── Holdback tracking ─────────────────────────────────────────────────────
  // Deducted: sum of ic.holdback on PROGRESS components (component !== "holdback")
  // These are the 10% retentions withheld from each draw invoice.
  const totalHoldbackDeducted = allInvoiceComponents
    .filter((ic) => ic.component !== "holdback")
    .reduce((s, ic) => s + (parseFloat(ic.holdback) || 0), 0);

  // Released: sum of ic.amount on HOLDBACK RELEASE components (component === "holdback")
  // These are separate invoices issued to return the retained holdback to the client.
  // We use ic.amount (not ic.total) to stay tax-exclusive and match the billed calc.
  const totalHoldbackReleased = allInvoiceComponents
    .filter((ic) => ic.component === "holdback")
    .reduce((s, ic) => s + (parseFloat(ic.amount) || 0), 0);

  // Fallback for non-QB users: estimate from SOV-level withHoldback flag
  const totalHoldbackDeductedFallback = sovList
    .filter((s) => ["invoiced", "paid"].includes(s.status))
    .reduce((s, sov) => {
      if (!sov.withHoldback) return s;
      const subtotal = (sov.items || [])
        .filter((i) => !i.changeOrderNumber || true) // all items
        .reduce(
          (a, item) => a + (parseFloat(item.workCompletedThisPeriod) || 0),
          0,
        );
      return s + subtotal * 0.1;
    }, 0);

  // Net holdback still retained (owed to us but not yet invoiced/released)
  const effectiveHoldbackDeducted =
    totalHoldbackDeducted > 0
      ? totalHoldbackDeducted
      : totalHoldbackDeductedFallback;
  const netHoldbackRetained = Math.max(
    0,
    effectiveHoldbackDeducted - totalHoldbackReleased,
  );

  // ── Labour WO groups ──────────────────────────────────────────────────────
  const pieceworkWOs = labourWOs.filter((w) => w.type === "piecework");
  const hourlyWOs = labourWOs.filter((w) => w.type === "hourly");
  const subcontractorWOs = labourWOs.filter((w) => w.type === "subcontractor");

  const getContractRate = (wo) => {
    const cli = wo.selectedContractLabourItem;
    if (!cli) return null;
    const uom = (cli.uom || "").toLowerCase();
    if (!["hr", "hour", "hrs"].includes(uom)) return null;
    return cli.unitCost || cli.unitRate || null;
  };

  if (loading) {
    return (
      <div className="bg-gray-900 rounded-xl p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-400 text-sm">Loading WIP data…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 rounded-xl p-1">
      <div className="max-w-full mx-auto px-4 py-6 space-y-6">
        {/* ── Page header ── */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">
              Work In Progress
            </h1>
            <p className="text-sm text-gray-400 mt-0.5">
              Project cost tracking — charges, pending, and remaining budget
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-sm text-gray-200 bg-gray-700 rounded-md hover:bg-gray-600 border border-gray-600"
          >
            ← Back
          </button>
        </div>

        {/* ── Top summary strip (existing) ── */}
        <div className="bg-gray-800 rounded-xl border border-gray-600 overflow-hidden shadow-xl">
          <div className="px-6 py-4 border-b border-gray-700">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
              <SummaryTile
                label="Total Estimated Project Cost"
                value={fmt(totalBudget)}
                color="text-white"
              />
              <SummaryTile
                label="Cost Incurred"
                value={fmt(totalCharged)}
                sub={`${overallPct.toFixed(1)}% of budget`}
                color={
                  totalCharged > totalBudget ? "text-red-400" : "text-green-400"
                }
              />
              <SummaryTile
                label="Committed Costs"
                value={fmt(totalPending)}
                color="text-yellow-400"
              />
              <SummaryTile
                label="Remaining"
                value={fmt(totalRemaining)}
                sub="Est. Project Cost − Incurred − Committed"
                color="text-orange-300"
              />
              {coImpact + varianceImpact > 0 && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
                    Estimated Change Costs
                  </span>
                  <span className="text-sm font-bold text-purple-400">
                    +{fmt(coImpact + varianceImpact)}
                  </span>
                  {coImpact > 0 && (
                    <span className="text-[10px] text-gray-400">
                      Change Orders: {fmt(coImpact)}
                    </span>
                  )}
                  {varianceImpact > 0 && (
                    <span className="text-[10px] text-gray-400">
                      Variances: {fmt(varianceImpact)}
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Overall progress bar */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-2">
              <span>Overall project cost progress</span>
              <span>
                {overallPct.toFixed(1)}% incurred
                {totalPending > 0 &&
                  ` · +${pct(totalPending, totalBudget).toFixed(1)}% committed`}
              </span>
            </div>
            <CostBar
              budget={totalBudget}
              charged={totalCharged}
              pending={totalPending}
              height="h-4"
            />
            <div className="flex items-center gap-5 mt-2">
              <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />
                Cost Incurred
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="w-2.5 h-2.5 rounded-sm bg-yellow-400/80 inline-block" />
                Committed Costs
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                <span className="w-2.5 h-2.5 rounded-sm bg-gray-700 border border-gray-600 inline-block" />
                Remaining
              </span>
            </div>
          </div>

          {/* Section breakdowns */}
          <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-700 border-t border-gray-700">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  Labour
                </span>
                <span className="text-xs text-gray-400">
                  {labourWOs.length} work orders
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <SummaryTile
                  label="Budget"
                  value={fmt(labourTotals.budget)}
                  small
                  color="text-gray-200"
                />
                <SummaryTile
                  label="Cost Incurred"
                  value={fmt(labourTotals.charged)}
                  small
                  color="text-green-400"
                />
                <SummaryTile
                  label="Committed"
                  value={fmt(labourTotals.pending)}
                  small
                  color="text-yellow-400"
                />
                <SummaryTile
                  label="Remaining"
                  value={fmt(labourTotals.remaining)}
                  small
                  color="text-orange-300"
                />
              </div>
              <div className="mt-2">
                <CostBar
                  budget={labourTotals.budget}
                  charged={labourTotals.charged}
                  pending={labourTotals.pending}
                  height="h-1.5"
                />
              </div>
            </div>
            <div className="px-6 py-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-[11px] font-semibold text-gray-400 uppercase tracking-widest">
                  Materials
                </span>
                <span className="text-xs text-gray-400">
                  {poSummaries.length} purchase orders
                </span>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <SummaryTile
                  label="Budget"
                  value={fmt(matTotals.budget)}
                  small
                  color="text-gray-200"
                />
                <SummaryTile
                  label="Cost Incurred"
                  value={fmt(matTotals.ordered)}
                  small
                  color="text-green-400"
                />
                <SummaryTile
                  label="Committed"
                  value={fmt(matTotals.pending)}
                  small
                  color="text-yellow-400"
                />
                <SummaryTile
                  label="Remaining"
                  value={fmt(matTotals.remaining)}
                  small
                  color="text-orange-300"
                />
              </div>
              <div className="mt-2">
                <CostBar
                  budget={matTotals.budget}
                  charged={matTotals.ordered}
                  pending={matTotals.pending}
                  height="h-1.5"
                />
              </div>
            </div>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════════════════
            ── NEW: Contract & Billing Overview card ──
            Paste this entire block between the top summary strip above
            and the tab nav below. Nothing else needs to move.
        ═══════════════════════════════════════════════════════════════════ */}
        <div className="bg-gray-800 rounded-xl border border-gray-600 overflow-hidden shadow-xl">
          {/* Section heading */}
          <div className="px-6 py-3 border-b border-gray-700 flex items-center gap-2">
            <span className="text-sm font-bold text-white tracking-tight">
              Contract &amp; Billing
            </span>
            <span className="text-[10px] text-gray-400 uppercase tracking-widest">
              Revenue overview
            </span>
          </div>

          {/* ── Row 1: Contract value tiles ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-700 border-b border-gray-700">
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Original Contract
              </div>
              <div className="text-lg font-bold text-gray-200">
                {fmt(originalContract)}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Approved CO Value
              </div>
              <div className="text-lg font-bold text-purple-400">
                {approvedCOValue > 0 ? `+${fmt(approvedCOValue)}` : fmt(0)}
              </div>
              <div className="text-[10px] text-gray-400">
                {approvedCOs.length} change order
                {approvedCOs.length !== 1 ? "s" : ""}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Current Contract Value
              </div>
              <div className="text-lg font-bold text-white">
                {fmt(currentContractValue)}
              </div>
              <div className="text-[10px] text-gray-400">
                Original + approved COs
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Pending CO Value
              </div>
              <div className="text-lg font-bold text-yellow-400">
                {pendingCOValue > 0 ? `+${fmt(pendingCOValue)}` : "—"}
              </div>
              <div className="text-[10px] text-gray-400">
                {pendingCOs.length} pending approval
              </div>
            </div>
          </div>

          {/* ── Row 2: Change order breakdown — collapsible ── */}
          {changeOrdersFull.length > 0 && (
            <div className="border-b border-gray-700">
              {/* Clickable header row — always visible */}
              <button
                type="button"
                onClick={() => setCoBreakdownOpen((v) => !v)}
                className="w-full px-5 py-3 flex items-center justify-between hover:bg-gray-700/30 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold">
                    Change Order Breakdown
                  </span>
                  {/* Inline summary pills — visible when collapsed */}
                  {!coBreakdownOpen && (
                    <div className="flex items-center gap-2">
                      {approvedCOs.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-green-900/40 text-green-400 border-green-700">
                          {approvedCOs.length} approved · {fmt(approvedCOValue)}
                        </span>
                      )}
                      {pendingCOs.length > 0 && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border bg-yellow-900/30 text-yellow-400 border-yellow-700/50">
                          {pendingCOs.length} pending · {fmt(pendingCOValue)}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <svg
                  className={`w-4 h-4 text-gray-400 shrink-0 transition-transform duration-200 ${coBreakdownOpen ? "rotate-180" : ""}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>

              {/* Expandable table */}
              {coBreakdownOpen && (
                <div className="overflow-x-auto border-t border-gray-700">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-900/60">
                      <tr>
                        {[
                          "#",
                          "Title",
                          "Status",
                          "Budget Cost",
                          "Customer Price",
                        ].map((h) => (
                          <th
                            key={h}
                            className="px-4 py-2 text-left text-[10px] font-semibold text-gray-400 uppercase whitespace-nowrap first:pl-5"
                          >
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-700/50">
                      {changeOrdersFull.map((co) => {
                        // Use signed calc: negative deltaQty = cost reduction
                        const budgetCost = (co.items || [])
                          .filter((i) => i.forBudget !== false)
                          .reduce((s, i) => s + calcCOItemSignedCost(i), 0);
                        const isApproved = co.approvalStatus === "Approved";
                        const isPending = co.approvalStatus === "Pending";
                        return (
                          <tr key={co._id} className="hover:bg-gray-700/30">
                            <td className="px-4 py-2 pl-5 text-gray-400 font-medium whitespace-nowrap">
                              CO-
                              {String(co.changeOrderNumber || "").padStart(
                                3,
                                "0",
                              )}
                            </td>
                            <td
                              className="px-4 py-2 text-gray-200 max-w-[280px] truncate"
                              title={co.title}
                            >
                              {co.title}
                            </td>
                            <td className="px-4 py-2">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-semibold border ${
                                  isApproved
                                    ? "bg-green-900/40 text-green-400 border-green-700"
                                    : isPending
                                      ? "bg-yellow-900/30 text-yellow-400 border-yellow-700/50"
                                      : "bg-red-900/30 text-red-400 border-red-700/50"
                                }`}
                              >
                                {co.approvalStatus}
                              </span>
                            </td>
                            <td
                              className={`px-4 py-2 text-right font-medium ${
                                !isApproved && !isPending
                                  ? "text-gray-500"
                                  : budgetCost < 0
                                    ? "text-red-400"
                                    : "text-green-400"
                              }`}
                            >
                              {budgetCost === 0
                                ? "—"
                                : budgetCost < 0
                                  ? `-${fmt(Math.abs(budgetCost))}`
                                  : `+${fmt(budgetCost)}`}
                            </td>
                            <td
                              className={`px-4 py-2 text-right font-semibold ${
                                isApproved
                                  ? "text-gray-200"
                                  : isPending
                                    ? "text-yellow-400"
                                    : "text-gray-500 line-through"
                              }`}
                            >
                              {fmt(co.totalCustomerPrice || co.totalPrice || 0)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {approvedCOs.length > 0 && (
                      <tfoot>
                        <tr className="border-t border-gray-600">
                          <td
                            colSpan={3}
                            className="px-4 py-2 pl-5 text-[10px] text-gray-400 font-semibold uppercase"
                          >
                            Approved totals
                          </td>
                          <td
                            className={`px-4 py-2 text-right font-bold ${approvedCOCost < 0 ? "text-red-400" : "text-green-400"}`}
                          >
                            {approvedCOCost === 0
                              ? "—"
                              : approvedCOCost < 0
                                ? `-${fmt(Math.abs(approvedCOCost))}`
                                : `+${fmt(approvedCOCost)}`}
                          </td>
                          <td className="px-4 py-2 text-right text-gray-200 font-bold">
                            {fmt(approvedCOValue)}
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}
            </div>
          )}

          {/* ── Row 3: Billing tiles ── */}
          <div className="grid grid-cols-2 md:grid-cols-5 divide-y md:divide-y-0 md:divide-x divide-gray-700 border-b border-gray-700">
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Total Billed
              </div>
              <div className="text-lg font-bold text-green-400">
                {fmt(effectiveTotalBilled)}
              </div>
              <div className="text-[10px] text-gray-400">
                {billingPct.toFixed(1)}% of contract ·{" "}
                {sovList.filter((s) =>
                  (s.invoiceComponents || []).some(
                    (ic) => ic.status !== "voided",
                  ),
                ).length ||
                  sovList.filter((s) => ["invoiced", "paid"].includes(s.status))
                    .length}{" "}
                draws
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Holdback Retained
              </div>
              <div
                className={`text-lg font-bold ${netHoldbackRetained > 0 ? "text-purple-400" : "text-gray-400"}`}
              >
                {fmt(netHoldbackRetained)}
              </div>
              <div className="text-[10px] text-gray-400">
                {fmt(effectiveHoldbackDeducted)} deducted
                {totalHoldbackReleased > 0 &&
                  ` · ${fmt(totalHoldbackReleased)} released`}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Total Paid
              </div>
              <div className="text-lg font-bold text-blue-400">
                {fmt(totalPaid)}
              </div>
              <div className="text-[10px] text-gray-400">
                {effectiveTotalBilled > 0
                  ? `${((totalPaid / effectiveTotalBilled) * 100).toFixed(1)}% of billed`
                  : "No draws invoiced"}
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                Accounts Receivable
              </div>
              <div
                className={`text-lg font-bold ${accountsReceivable > 0 ? "text-orange-300" : "text-gray-400"}`}
              >
                {fmt(accountsReceivable)}
              </div>
              <div className="text-[10px] text-gray-400">
                Billed but not yet paid
              </div>
            </div>
            <div className="px-5 py-4">
              <div className="text-[10px] text-gray-400 uppercase tracking-widest font-semibold mb-1">
                WIP Completion
              </div>
              <div className="text-lg font-bold text-orange-400">
                {wipCompletionPct.toFixed(1)}%
              </div>
              <div className="text-[10px] text-gray-400">
                Cost incurred / budget
              </div>
            </div>
          </div>

          {/* ── Row 4: Over/underbilled alert banner ── */}
          {/* Only shows when the gap is more than 2% — within 2% is considered balanced */}
          {Math.abs(billingGapPct) > 2 && (
            <div
              className={`mx-5 my-3 px-4 py-3 rounded-lg flex items-start gap-3 border ${
                billingGapPct > 0
                  ? "bg-red-900/20 border-red-700/40"
                  : "bg-yellow-900/20 border-yellow-700/40"
              }`}
            >
              <div
                className={`mt-0.5 text-sm flex-shrink-0 ${
                  billingGapPct > 0 ? "text-red-400" : "text-yellow-400"
                }`}
              >
                ⚠
              </div>
              <div>
                <div
                  className={`text-xs font-semibold ${
                    billingGapPct > 0 ? "text-red-400" : "text-yellow-400"
                  }`}
                >
                  {billingGapPct > 0
                    ? `Overbilled — ${Math.abs(billingGapPct).toFixed(1)}% ahead of completion`
                    : `Underbilled — ${Math.abs(billingGapPct).toFixed(1)}% behind completion`}
                </div>
                <div className="text-[11px] text-gray-400 mt-0.5">
                  {billingGapPct > 0
                    ? `Billed ${billingPct.toFixed(1)}% of contract but WIP progress is ${wipCompletionPct.toFixed(1)}%. You are billing ahead of work completed by ~${fmt(Math.abs(billingGapAmt))}.`
                    : `Billed ${billingPct.toFixed(1)}% of contract but WIP progress is ${wipCompletionPct.toFixed(1)}%. Approximately ${fmt(Math.abs(billingGapAmt))} of completed work has not been invoiced yet.`}
                </div>
              </div>
            </div>
          )}

          {/* ── Row 5: Dual progress bars — billing vs completion ── */}
          <div className="px-5 py-4">
            <div className="flex items-center justify-between text-[11px] text-gray-400 mb-3">
              <span>Billing vs completion comparison</span>
              <span>Contract: {fmt(currentContractValue)}</span>
            </div>
            <div className="space-y-2.5">
              {/* Billing bar */}
              <div>
                <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                  <span>Billed to date</span>
                  <span className="text-green-400 font-medium">
                    {fmt(effectiveTotalBilled)} · {billingPct.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-green-500 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, billingPct)}%` }}
                  />
                </div>
              </div>
              {/* WIP completion bar */}
              <div>
                <div className="flex justify-between text-[10px] text-gray-400 mb-1">
                  <span>WIP completion</span>
                  <span className="text-orange-400 font-medium">
                    {fmt(totalCharged)} · {wipCompletionPct.toFixed(1)}%
                  </span>
                </div>
                <div className="w-full h-2 bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-orange-400 rounded-full transition-all duration-500"
                    style={{ width: `${Math.min(100, wipCompletionPct)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Legend + billing health indicator */}
            <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
              <div className="flex items-center gap-5">
                <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-sm bg-green-500 inline-block" />
                  Billed to date
                </span>
                <span className="flex items-center gap-1.5 text-[11px] text-gray-400">
                  <span className="w-2.5 h-2.5 rounded-sm bg-orange-400 inline-block" />
                  WIP completion
                </span>
              </div>
              {/* Billing health pill */}
              <span
                className={`text-[10px] font-semibold px-2.5 py-1 rounded-full border ${
                  Math.abs(billingGapPct) <= 2
                    ? "bg-green-900/30 text-green-400 border-green-700/50"
                    : billingGapPct > 0
                      ? "bg-red-900/30 text-red-400 border-red-700/50"
                      : "bg-yellow-900/30 text-yellow-400 border-yellow-700/50"
                }`}
              >
                {Math.abs(billingGapPct) <= 2
                  ? "Billing on track"
                  : billingGapPct > 0
                    ? `Overbilled ${billingGapPct.toFixed(1)}%`
                    : `Underbilled ${Math.abs(billingGapPct).toFixed(1)}%`}
              </span>
            </div>
          </div>
        </div>
        {/* ═══════════════════════════════════════════════════════════════════
            ── END: Contract & Billing Overview card ──
        ═══════════════════════════════════════════════════════════════════ */}

        {/* ── Tab nav ── */}
        <div className="flex gap-1 bg-gray-800 p-1 rounded-lg border border-gray-700 w-fit">
          {[
            { key: "labour", label: `Labour (${labourWOs.length})` },
            { key: "materials", label: `Materials (${poSummaries.length})` },
          ].map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-5 py-2 rounded-md text-sm font-medium transition-colors ${
                activeTab === tab.key
                  ? "bg-orange-500 text-white shadow"
                  : "text-gray-400 hover:text-white hover:bg-gray-700"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Labour tab ── */}
        {activeTab === "labour" && (
          <div className="space-y-6">
            {/* Piecework */}
            {pieceworkWOs.length > 0 && (
              <div>
                <SectionHeader
                  title="Piecework"
                  icon="🔧"
                  count={pieceworkWOs.length}
                  budget={pieceworkWOs.reduce((s, w) => s + w.budgetAmt, 0)}
                  charged={pieceworkWOs.reduce((s, w) => s + w.chargedAmt, 0)}
                  pending={pieceworkWOs.reduce((s, w) => s + w.pendingAmt, 0)}
                  remaining={pieceworkWOs.reduce(
                    (s, w) => s + w.remainingAmt,
                    0,
                  )}
                />
                <div className="space-y-2">
                  {pieceworkWOs.map((wo) => {
                    const key = `pw-${wo._id}`;
                    return (
                      <AccordionCard
                        key={key}
                        isOpen={!!openAccordions[key]}
                        onToggle={() => toggleAccordion(key)}
                        header={
                          <WOAccordionHeader
                            wo={wo}
                            budgetAmt={wo.budgetAmt}
                            chargedAmt={wo.chargedAmt}
                            pendingAmt={wo.pendingAmt}
                            remainingAmt={wo.remainingAmt}
                          />
                        }
                      >
                        <PieceworkDrillDown
                          woId={wo._id}
                          projectId={projectId}
                          token={token}
                        />
                      </AccordionCard>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Hourly */}
            {hourlyWOs.length > 0 && (
              <div>
                <SectionHeader
                  title="Hourly"
                  icon="⏱"
                  count={hourlyWOs.length}
                  budget={hourlyWOs.reduce((s, w) => s + w.budgetAmt, 0)}
                  charged={hourlyWOs.reduce((s, w) => s + w.chargedAmt, 0)}
                  pending={hourlyWOs.reduce((s, w) => s + w.pendingAmt, 0)}
                  remaining={hourlyWOs.reduce((s, w) => s + w.remainingAmt, 0)}
                />
                <div className="space-y-2">
                  {hourlyWOs.map((wo) => {
                    const key = `hr-${wo._id}`;
                    const rate = getContractRate(wo);
                    const woStats = hourlyStats[wo._id?.toString()];
                    return (
                      <AccordionCard
                        key={key}
                        isOpen={!!openAccordions[key]}
                        onToggle={() => toggleAccordion(key)}
                        header={
                          <HourlyWOAccordionHeader
                            wo={wo}
                            budgetAmt={wo.budgetAmt}
                            chargedAmt={wo.chargedAmt}
                            pendingAmt={wo.pendingAmt}
                            remainingAmt={wo.remainingAmt}
                            stats={woStats}
                          />
                        }
                      >
                        <HourlyDrillDown
                          woId={wo._id}
                          projectId={projectId}
                          token={token}
                          contractHourlyRate={rate}
                        />
                      </AccordionCard>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Subcontractor */}
            {subcontractorWOs.length > 0 && (
              <div>
                <SectionHeader
                  title="Subcontractor"
                  icon="🏗"
                  count={subcontractorWOs.length}
                  budget={subcontractorWOs.reduce((s, w) => s + w.budgetAmt, 0)}
                  charged={subcontractorWOs.reduce(
                    (s, w) => s + w.chargedAmt,
                    0,
                  )}
                  pending={subcontractorWOs.reduce(
                    (s, w) => s + w.pendingAmt,
                    0,
                  )}
                  remaining={subcontractorWOs.reduce(
                    (s, w) => s + w.remainingAmt,
                    0,
                  )}
                />
                <div className="space-y-2">
                  {subcontractorWOs.map((wo) => {
                    const key = `sc-${wo._id}`;
                    return (
                      <AccordionCard
                        key={key}
                        isOpen={!!openAccordions[key]}
                        onToggle={() => toggleAccordion(key)}
                        header={
                          <WOAccordionHeader
                            wo={wo}
                            budgetAmt={wo.budgetAmt}
                            chargedAmt={wo.chargedAmt}
                            pendingAmt={wo.pendingAmt}
                            remainingAmt={wo.remainingAmt}
                          />
                        }
                      >
                        <SubcontractorDrillDown
                          woId={wo._id}
                          projectId={projectId}
                          token={token}
                        />
                      </AccordionCard>
                    );
                  })}
                </div>
              </div>
            )}

            {labourWOs.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                No work orders found for this project.
              </div>
            )}
          </div>
        )}

        {/* ── Materials tab ── */}
        {activeTab === "materials" && (
          <div>
            <SectionHeader
              title="Purchase Orders"
              icon="📦"
              count={poSummaries.length}
              budget={matTotals.budget}
              charged={matTotals.ordered}
              pending={matTotals.pending}
              remaining={matTotals.remaining}
            />
            <div className="space-y-2">
              {poSummaries.map((po) => {
                const key = `po-${po._id}`;
                return (
                  <AccordionCard
                    key={key}
                    isOpen={!!openAccordions[key]}
                    onToggle={() => toggleAccordion(key)}
                    header={
                      <POAccordionHeader
                        po={po}
                        budgetAmt={po.budgetAmt}
                        orderedAmt={po.orderedAmt}
                        pendingAmt={po.pendingAmt}
                        remainingAmt={po.remainingAmt}
                      />
                    }
                  >
                    <MaterialDrillDown
                      poId={po._id}
                      projectId={projectId}
                      token={token}
                    />
                  </AccordionCard>
                );
              })}
              {poSummaries.length === 0 && (
                <div className="text-center py-12 text-gray-400">
                  No purchase orders found for this project.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
